import React from "react";

/** SkeletonCard — placeholder for a single list item (rounded-list) */
export function SkeletonCard({ height = "h-[72px]", className = "" }) {
  return (
    <div
      className={`bg-slate-200 dark:bg-slate-700 rounded-list ${height} animate-pulse ${className}`}
    />
  );
}

/** SkeletonList — stack of SkeletonCard placeholders */
export function SkeletonList({ count = 3, height, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: count }, (_, i) => (
        <SkeletonCard key={i} height={height} />
      ))}
    </div>
  );
}

/** SkeletonListItem — rich list card placeholder that mirrors the visual structure
 *  of standard list cards: rounded-[24px] card with title + subtitle + trailing pill.
 *  Use instead of inline animate-pulse divs in list pages. */
export function SkeletonListItem({ className = "" }) {
  return (
    <div
      className={`bg-white dark:bg-slate-800 p-4 rounded-[24px] border border-slate-200/50 dark:border-slate-700/50 shadow-card animate-pulse flex items-center justify-between gap-3 ${className}`}
    >
      <div className="flex-1 space-y-2 min-w-0">
        <div className="h-3.5 w-[60%] bg-slate-200 dark:bg-slate-700 rounded-full" />
        <div className="h-3 w-[40%] bg-slate-100 dark:bg-slate-700/60 rounded-full" />
      </div>
      <div className="h-6 w-14 bg-slate-100 dark:bg-slate-700/60 rounded-full shrink-0" />
    </div>
  );
}

/** SkeletonListItemWithIcon — list card with a leading icon box + text lines.
 *  Matches pages like Prayers, Notes that have a rounded icon on the left. */
export function SkeletonListItemWithIcon({ className = "" }) {
  return (
    <div
      className={`bg-white dark:bg-slate-800 rounded-[24px] p-4 border border-slate-200/50 dark:border-slate-700/60 shadow-card animate-pulse flex items-start gap-3 ${className}`}
    >
      <div className="h-11 w-11 rounded-[14px] bg-slate-200 dark:bg-slate-700 shrink-0" />
      <div className="flex-1 space-y-2 pt-0.5">
        <div className="h-3.5 w-[70%] bg-slate-200 dark:bg-slate-700 rounded-full" />
        <div className="h-3 w-full bg-slate-100 dark:bg-slate-700/60 rounded-full" />
        <div className="h-3 w-[50%] bg-slate-100 dark:bg-slate-700/60 rounded-full" />
      </div>
    </div>
  );
}

/** SkeletonVisitCard — list card placeholder matching the Visit card layout:
 *  date column | divider | text lines | status box. */
export function SkeletonVisitCard({ className = "" }) {
  return (
    <div className={`flex items-center gap-3.5 bg-white dark:bg-slate-800 rounded-[20px] border border-slate-200/50 dark:border-slate-700/50 animate-pulse overflow-hidden ${className}`}>
      <div className="flex flex-col items-center justify-center w-14 py-3.5 pl-4 gap-1 shrink-0">
        <div className="h-2 w-6 bg-slate-200 dark:bg-slate-700 rounded-full" />
        <div className="h-5 w-7 bg-slate-200 dark:bg-slate-700 rounded" />
      </div>
      <div className="w-px self-stretch bg-slate-100 dark:bg-slate-700/60 my-3 shrink-0" />
      <div className="flex-1 py-3.5 space-y-2">
        <div className="h-3.5 w-2/3 bg-slate-200 dark:bg-slate-700 rounded-full" />
        <div className="h-2.5 w-1/3 bg-slate-100 dark:bg-slate-700/50 rounded-full" />
      </div>
      <div className="h-8 w-8 rounded-[10px] bg-slate-100 dark:bg-slate-700 mr-4 shrink-0" />
    </div>
  );
}

/** SkeletonHero — placeholder for HeroCard / PageHero */
export function SkeletonHero({ className = "" }) {
  return (
    <div className={`mx-3 sm:mx-4 rounded-hero bg-slate-200 dark:bg-slate-700 h-[120px] animate-pulse ${className}`} />
  );
}

/** SkeletonForm — placeholder for a form page */
export function SkeletonForm({ rows = 4, className = "" }) {
  return (
    <div className={`px-4 space-y-4 ${className}`}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="space-y-1.5">
          <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" />
          <div className="h-12 w-full bg-slate-100 dark:bg-slate-700/60 rounded-icon animate-pulse" />
        </div>
      ))}
    </div>
  );
}
